import type { ReactNode } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { joinCss } from '@/shared/lib/css-utils';
import { useCollapsedDaysStore } from '../model/use-collapsed-days-store';
import css from './day-bucket.module.scss';

// Every day registers as a droppable container (REGARDLESS of whether
// it has items) so the orchestrator's two-step collision strategy can
// resolve "which day is the cursor over" in step 1, then "closest
// item within that day" in step 2. Without this, dragging across a
// boundary where DOM reflows would let `closestCenter` resolve to an
// item in a third (unintended) day and trigger a re-parent loop.

type DayBucketProps = {
  dayIndex: number;
  itemIds: string[];
  isEmpty: boolean;
  children: ReactNode;
  // Replaces the default "Drag an item here" hint for an empty day —
  // the inline gap filler renders here so an empty day still offers
  // "Suggest with AI" / "Add item here" (#262).
  emptySlot?: ReactNode;
  // Hosts that are not the planner day-list (e.g. advisor rail) pass
  // their own droppable id so they don't collide with `day-<index>`.
  droppableId?: string;
  droppableData?: Record<string, unknown>;
  // Advisor rail shares this chrome but not the planner collapse store.
  ignoreCollapsed?: boolean;
};

export const DayBucket = ({
  dayIndex,
  itemIds,
  isEmpty,
  children,
  emptySlot,
  droppableId,
  droppableData,
  ignoreCollapsed = false,
}: DayBucketProps) => {
  const collapsed = useCollapsedDaysStore((s) => s.collapsedDays.has(dayIndex));
  const isCollapsed = ignoreCollapsed ? false : collapsed;
  const droppable = useDroppable({
    id: droppableId ?? `day-${dayIndex}`,
    data: droppableData ?? ({ kind: 'day', dayIndex } as const),
  });

  return (
    <div
      className={joinCss(
        css.root,
        droppable.isOver && css.rootIsOver,
        isEmpty && !isCollapsed && css.rootIsEmpty
      )}
      ref={droppable.setNodeRef}
    >
      {!isCollapsed && (
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {children}
        </SortableContext>
      )}
      {isEmpty &&
        !isCollapsed &&
        (emptySlot ?? <div className={css.emptyHint}>Drag an item here</div>)}
    </div>
  );
};
